import {AfterViewInit, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FormHelperService} from "../../../shared/services/form-helper.service";
import {FileManagerService} from "../../../shared/services/file-manager.service";
import {HttpErrorResponse} from "@angular/common/http";
import {FileManager} from "../../../shared/interface/file-manager";
import {of, ReplaySubject} from "rxjs";
import {delay, filter, takeUntil, tap} from "rxjs/operators";
import {DOC_ORIENTATION} from "ngx-image-compress/lib/models/DOC_ORIENTATION";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {AuthService} from "../../../shared/services/auth.service";

export interface CatalogAddDialogData {
  catalogLocation?: string;
  catalog?: FileManager;
}

@Component({
  selector: 'app-add-catalog-dialog',
  templateUrl: './add-catalog-dialog.component.html',
  styleUrls: ['./add-catalog-dialog.component.scss']
})
export class AddCatalogDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  processingRequest = false;
  catalogNameControl: FormControl;
  fileForm: FormGroup;
  permissionTagPartnerPartnerControl:  FormControl;
  permissionRegistrationPartnerListControl:  FormControl;
  permissionTagPartnerOfwcaControl:  FormControl;
  permissionRegistrationOfwcaListControl:  FormControl;
  permissionPartnerControl:  FormControl;
  permissionOfwcaControl:  FormControl;
  permissionUserControl:  FormControl;
  permissionUserListControl:  FormControl;
  permissionDepartmentsUserListControl:  FormControl;
  publicControl: FormControl;
  fileControl:  FormControl;
  tagControl:  FormControl;
  contentControl:  FormControl;
  pageControl:  FormControl;
  formErrors: any = {};
  catalog?: FileManager;
  fileEdit = false;
  userPermissions: ReplaySubject<UserPermissions>;
  permissionCompanyPartnerPartnerControl: FormControl;
  permissionCompanyPartnerOfwcaControl: FormControl;


  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private initialized: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public dialogRef: MatDialogRef<CatalogAddDialogData>,
    private fb: FormBuilder,
    private snack: MatSnackBar,
    private formHelper: FormHelperService,
    private fileManagerService: FileManagerService,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: CatalogAddDialogData,
  ) {
    this.userPermissions = authService.getUserPermissions();
    this.catalog = data.catalog ?? null;
    this.fileControl = fb.control([]);
    this.fileForm = this.fb.group({
      catalogName: this.catalogNameControl = this.fb.control('', [Validators.required]),
      catalogLocation: this.fb.control(data.catalogLocation, []),
      permissionPartner: this.permissionPartnerControl = this.fb.control(false),
      permissionOfwca: this.permissionOfwcaControl = this.fb.control(false),
      permissionUser: this.permissionUserControl = this.fb.control(false),
      permissionUserList: this.permissionUserListControl = this.fb.control([]),
      permissionDepartmentsUserList: this.permissionDepartmentsUserListControl = this.fb.control([]),
      permissionTagPartnerPartner: this.permissionTagPartnerPartnerControl = this.fb.control([]),
      permissionRegistrationPartnerList: this.permissionRegistrationPartnerListControl = this.fb.control([]),
      permissionTagPartnerOfwca: this.permissionTagPartnerOfwcaControl = this.fb.control([]),
      permissionRegistrationOfwcaList: this.permissionRegistrationOfwcaListControl = this.fb.control([]),
      tag: this.tagControl = this.fb.control([]),
      filename: fb.control(''),
      content: this.contentControl = fb.control(''),
      page: this.pageControl = this.fb.control(''),
      public: this.publicControl = fb.control( false , []),
      permissionCompanyPartnerPartner: this.permissionCompanyPartnerPartnerControl = this.fb.control([]),
      permissionCompanyPartnerOfwca: this.permissionCompanyPartnerOfwcaControl = this.fb.control([]),
    });
  }

  ngOnInit(): void {
    this.fileControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        filter((files?: File[]) => files?.length > 0)
      )
      .subscribe((file: File[]) => {
        this.fileForm.get('filename').setValue(file[0].name);
        this.fileForm.get('content').reset('');

        const reader = new FileReader();
        reader.readAsDataURL(file[0]);
        reader.onload = () => {
          this.fileForm.get('content').setValue(/base64,(.+)/.exec(reader.result.toString())[1]);
        };
      });

    this.publicControl.valueChanges.subscribe(value => {
      if (value) {
        this.permissionPartnerControl.reset();
        this.permissionOfwcaControl.reset();
        this.permissionUserControl.reset();
        this.permissionUserListControl.reset();
        this.permissionDepartmentsUserListControl.reset();
        this.permissionTagPartnerPartnerControl.reset();
        this.permissionRegistrationPartnerListControl.reset();
        this.permissionTagPartnerOfwcaControl.reset();
        this.permissionRegistrationOfwcaListControl.reset();
        this.permissionCompanyPartnerPartnerControl.reset();
        this.permissionCompanyPartnerOfwcaControl.reset();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.catalog) {
      this.initialize(this.catalog);
    } else {
      this.initialized.next(true);
    }
  }

  sendForm(): void {
    if ( !this.fileForm.valid)
    {
      this.fileForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }
    this.processingRequest = true;

    this.formErrors = {};

    this.fileManagerService.sendCatalog(this.fileForm.value, this.catalog?.uuid)
      .subscribe(() => {
        this.snack.open( this.catalog ? 'Pomyślnie zaktualizowano folder' : 'Pomyślnie dodano folder', '', {
          duration: 2000,
        });
        this.processingRequest = false;

        this.dialogRef.close();

      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;
        this.snack.open('Podczas dodawania folderu wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });
        if (error.status === 400) {
          this.formErrors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.fileForm, this.formErrors);
        }
      })
    ;
  }

  closeDialog(e): void {
    this.dialogRef.close(e);
  }


  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initialize(file: FileManager): void {
    of(file)
        .pipe(
            delay(0),
            tap(() => {
              this.fileForm.reset({
                uuid: file.uuid,
                catalogName: file.name,
                catalogLocation: file.catalog,
                tag:  file.tagId?.split(',') ?? [],
                page: file.page,
                public: file.public,
                permissionPartner: file.permissionPartner,
                permissionOfwca: file.permissionOfwca,
                permissionUser: file.permissionUser,
                permissionUserList:  this.parseSerializedDataString(file.permissionUserList),
                permissionDepartmentsUserList:  this.parseSerializedDataString(file.permissionDepartmentsUserList),
                permissionTagPartnerPartner:  this.parseSerializedData(file.permissionTagPartnerPartner),
                permissionRegistrationPartnerList:  this.parseSerializedData(file.permissionRegistrationPartnerList),
                permissionTagPartnerOfwca:  this.parseSerializedData(file.permissionTagPartnerOfwca),
                permissionRegistrationOfwcaList:  this.parseSerializedData(file.permissionRegistrationOfwcaList),
                permissionCompanyPartnerPartner: this.parseSerializedData(file.permissionCompanyPartnerPartner),
                permissionCompanyPartnerOfwca: this.parseSerializedData(file.permissionCompanyPartnerOfwca),
              });
            }),
            delay(0),
            tap(() => this.initialized.next(true))
        ).subscribe();
  }

   parseSerializedData(serializedData: string): number[] {

     serializedData = serializedData.replace(/^a:\d+:/, '').slice(0, -1);

     const pairs = serializedData.split(';');

     const resultArray: number[] = [];

     for (const pair of pairs) {
         if (pair) {
             const parts = pair.split(':');
             if (parts[0] === 'd') {
                 resultArray.push(parseInt(parts[1], 10));
             }
         }
     }
     return resultArray;
   }

  parseSerializedDataString(serializedData: string): string[] {
    const uuidRegex = /s:\d+:"([a-f0-9-]{36})"/gi;
    const result: string[] = [];

    let match;
    while ((match = uuidRegex.exec(serializedData)) !== null) {
      result.push(match[1]);
    }

    return result;
  }
}
